// ============================================
// Chat Module - Load & Info Display (ES6)
// ============================================

import { openChatOverlay, closeMobileMenus } from './overlay.js';
import { loadChatList, refreshChatListBadges } from './list.js';
import {
  getCurrentUser,
  updateFixedMetaBar,
  setCurrentRoomId,
  setCurrentSessionId,
  setCurrentChatData,
  setCurrentTurnCount,
  getCurrentChatData
} from '../app/state.js';
import { clearMessages, appendMessageWithoutScroll, appendSceneImage } from './messaging/renderer.js';
import { setSelectedModel } from '../settings/state.js';
import { loadChatProfile } from '../profile/index.js';
import { updateModelNameDisplay } from './model-selector.js';
import { clearChoices } from './message-actions.js';
import { renderMemoryPanel, invalidateAllCache } from '../memory.js';
import { showToast, showGlobalLoading, hideGlobalLoading, escapeHtml } from '../utils.js';
import { useChatStatusStore } from '../../stores/chatStatusStore.js';
import { setupScrollObserver, cleanupScrollObserver } from '../shared/lazy-image.js';
import { resetPagination, setupScrollToLoadMore, cleanupPagination } from './messaging/pagination.js';
import { setupTTSForChat } from './tts/index.js';

// ============================================
// 메시지 페이지네이션 상수
// ============================================
const INITIAL_MESSAGE_LIMIT = 50;

// 스크롤 observer 인스턴스 (cleanup용)
let currentScrollObserver = null;

// ============================================
// loadSession - 세션 구조 채팅 로드
// ============================================

export async function loadSession(roomId, sessionId) {
  // 현재 room/session ID 설정
  setCurrentRoomId(roomId);
  setCurrentSessionId(sessionId);

  // 메모리 패널 캐시 전체 무효화 (세션 전환)
  invalidateAllCache();

  // 모바일 사이드바 닫기
  closeMobileMenus();

  // Update UI
  document.querySelectorAll('.chat-item').forEach(item => item.classList.remove('active'));
  document.querySelector(`.chat-item[data-room-id="${roomId}"]`)?.classList.add('active');

  // 로딩 표시
  showGlobalLoading();

  const db = firebase.firestore();
  const userId = getCurrentUser()?.uid;

  if (!userId) {
    hideGlobalLoading();
    showToast('로그인이 필요합니다', true);
    return;
  }

  let chatData, messages = [];
  try {
    const roomRef = db.collection('users').doc(userId).collection('chatRooms').doc(roomId);
    const sessionRef = roomRef.collection('sessions').doc(sessionId);

    // 병렬 로드: room, session, messages (최근 N개), memory, sceneImages
    const [roomDoc, sessionDoc, messagesSnap, worldDoc, relationshipDoc, sceneImagesSnap] = await Promise.all([
      roomRef.get(),
      sessionRef.get(),
      // 최근 메시지만 로드 (역순으로 가져온 후 정렬)
      sessionRef.collection('messages').orderBy('createdAt', 'desc').limit(INITIAL_MESSAGE_LIMIT).get(),
      sessionRef.collection('memory').doc('world').get(),
      sessionRef.collection('memory').doc('relationship').get(),
      sessionRef.collection('sceneImages').orderBy('createdAt', 'asc').get()
    ]);

    if (!sessionDoc.exists) {
      throw new Error('세션을 찾을 수 없습니다');
    }

    // room 데이터와 session 데이터 병합
    const roomData = roomDoc.exists ? roomDoc.data() : {};
    const sessionData = sessionDoc.data();

    chatData = {
      id: sessionId,
      roomId: roomId,
      sessionId: sessionId,
      ...roomData,  // character, mode, images 등 room 레벨 데이터
      ...sessionData,  // turnCount, settings, userProfile 등 session 레벨 데이터
      title: roomData.title || sessionData.name || '새 채팅',
      sessionName: sessionData.name || '상담'  // 메모리 패널에서 사용
    };

    // desc로 가져왔으므로 reverse하여 시간순 정렬
    messages = messagesSnap.docs.map(doc => ({ id: doc.id, type: 'message', ...doc.data() })).reverse();

    // sceneImages 병합
    const sceneImages = sceneImagesSnap.docs.map(doc => ({
      id: doc.id,
      type: 'sceneImage',
      ...doc.data()
    }));

    if (sceneImages.length > 0) {
      messages = [...messages, ...sceneImages].sort((a, b) => {
        const timeA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
        const timeB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
        return timeA - timeB;
      });
    }

    // Infinite Memory 데이터로 narrativeState 업데이트
    const worldData = worldDoc.exists ? worldDoc.data() : null;
    const relationshipData = relationshipDoc.exists ? relationshipDoc.data() : null;

    if (worldData?.current) {
      if (!chatData.narrativeState) {
        chatData.narrativeState = { location: {}, time: {}, currentScene: {} };
      }
      chatData.narrativeState.location = { current: worldData.current.location || '-' };
      chatData.narrativeState.time = { description: worldData.current.time || '-', period: worldData.current.time };
      chatData.narrativeState.currentScene = {
        description: worldData.current.situation || '',
        mood: worldData.current.mood || ''
      };
    }

    if (relationshipData?.current && chatData.character) {
      chatData.character.relationship = relationshipData.current.description || chatData.character.relationship;
    }
  } catch (e) {
    console.error('[loadSession] 오류:', e);
    hideGlobalLoading();
    showToast('세션 로드 실패', true);
    return;
  }

  // UI 렌더링 (loadChat과 동일한 렌더링 로직)
  try {
    clearMessages();

    // images.characters 배열에서 캐릭터 객체로 imageUrl 복원
    if (chatData?.images?.characters) {
      if (chatData.mode === 'story' && chatData.characters) {
        chatData.characters.forEach((char, idx) => {
          if (char && chatData.images.characters[idx]) {
            char.imageUrl = chatData.images.characters[idx];
          }
        });
      } else if (chatData.mode === 'character' && chatData.character) {
        if (chatData.images.characters[0]) {
          chatData.character.imageUrl = chatData.images.characters[0];
        }
      }
    }

    setCurrentChatData(chatData);

    // TTS 설정 초기화 (캐릭터 voiceId 로드)
    setupTTSForChat();

    if (chatData?.settings?.model) {
      setSelectedModel(chatData.settings.model);
    } else {
      setSelectedModel('gemini-3-flash-preview');
    }

    updateModelNameDisplay();
    setCurrentTurnCount(chatData?.turnCount || 0);
    await loadChatProfile(chatData);

    // hasMore 미리 계산 (프로필 표시 조건에 사용)
    const hasMore = messages.length >= INITIAL_MESSAGE_LIMIT;

    // Show/hide world info button
    const worldBtn = document.getElementById('worldInfoBtn');
    if (worldBtn) {
      if (chatData?.mode === 'story' && chatData?.world) {
        worldBtn.style.display = 'flex';
        worldBtn._worldData = { world: chatData.world, characters: chatData.characters, scenario: chatData.scenario };
        // 모든 메시지 로드된 경우만 프로필 표시 (50개 미만이면 즉시 표시)
        if (!hasMore) {
          showWorldInfo(chatData.world, chatData.characters, chatData.scenario);
        }
      } else {
        worldBtn.style.display = 'none';
        // 모든 메시지 로드된 경우만 프로필 표시 (50개 미만이면 즉시 표시)
        if (chatData?.character && !hasMore) {
          showCharacterProfile(chatData.character, chatData.scenario);
        }
      }
    }

    // Render messages
    const container = document.getElementById('messagesContainer');
    if (container) {
      let isFirstAssistantMessage = true;
      messages.forEach(item => {
        const timestamp = item.createdAt?.toDate ? item.createdAt.toDate() : (item.createdAt ? new Date(item.createdAt) : null);

        if (item.type === 'sceneImage') {
          // 채팅창 내 상황 이미지는 원본 사용
          appendSceneImage(item.imageUrl, timestamp);
        } else {
          const isAI = item.role === 'assistant' || item.role === 'model';
          const isIntro = isAI && isFirstAssistantMessage;
          if (isAI) isFirstAssistantMessage = false;
          appendMessageWithoutScroll(item.role, item.content, timestamp, item.id, isIntro, item.turn);
        }
      });

      // 스크롤을 최하단으로 이동 (이미지 로드 대기 없이 즉시)
      const hasUserMessage = messages.some(item => item.role === 'user');
      if (hasUserMessage) {
        container.scrollTop = container.scrollHeight;
      }

      // 이전 observer 정리 후 새로 설정
      if (currentScrollObserver) {
        cleanupScrollObserver(currentScrollObserver);
      }
      // ResizeObserver로 이미지 로드 후 스크롤 자동 조정
      currentScrollObserver = setupScrollObserver(container);

      // 페이지네이션 설정 (더 이전 메시지 로드용)
      const oldestTimestamp = messages.length > 0 ? messages[0].createdAt : null;
      resetPagination(hasMore, oldestTimestamp);

      // 더 로드할 메시지가 있으면 무한 스크롤 설정
      if (hasMore) {
        setupScrollToLoadMore(container);
      }
    }

    const title = chatData?.title || '새 채팅';
    const chatTitleEl = document.getElementById('chatTitle');
    if (chatTitleEl) {
      chatTitleEl.textContent = title.length > 7 ? title.slice(0, 7) + '...' : title;
    }

    updateFixedMetaBar();
    renderMemoryPanel('state');
    clearChoices();

    // pending 상태 복원 (채팅 나갔다 다시 들어온 경우)
    const chatStatus = useChatStatusStore.getState();
    const pendingData = chatStatus.getPending(sessionId);
    if (pendingData) {
      showPendingResponseUI(pendingData, sessionId);
    }

    refreshChatListBadges();
  } catch (renderError) {
    console.error('[loadSession] 렌더링 오류:', renderError);
  }

  // 스크롤이 완전히 적용된 후 모달 열기 (딱 하는 현상 방지)
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      openChatOverlay();
      hideGlobalLoading();
    });
  });
  loadChatList().catch(e => console.error('loadChatList error:', e));
}

// ============================================
// Pending Response UI (레거시 loadChat 함수 삭제됨 - 2026-01-11)
// ============================================

const PENDING_TIMEOUT = 2 * 60 * 1000; // 2분
let pendingTimeoutId = null; // 타임아웃 타이머 ID

function showPendingResponseUI(pendingData, chatId) {
  const container = document.getElementById('messagesContainer');
  if (!container) return;

  // 기존 로딩 UI 및 타이머 제거
  document.getElementById('loadingMessage')?.remove();
  if (pendingTimeoutId) {
    clearTimeout(pendingTimeoutId);
    pendingTimeoutId = null;
  }

  const chatStatus = useChatStatusStore.getState();
  const elapsed = Date.now() - pendingData.startedAt;
  const isTimedOut = elapsed > PENDING_TIMEOUT;

  if (isTimedOut) {
    // 타임아웃 - pending 상태 해제만 (UI 표시 없음)
    chatStatus.clearPending(chatId);
    refreshChatListBadges();
    return;
  }

  // 로딩 애니메이션만 표시 (기존 showLoading과 동일)
  const div = document.createElement('div');
  div.className = 'message assistant';
  div.id = 'loadingMessage';
  div.innerHTML = `
    <div class="message-content">
      <div class="loading"><span></span><span></span><span></span></div>
    </div>
  `;
  container.appendChild(div);
  container.scrollTop = container.scrollHeight;

  // 남은 시간 후 자동 타임아웃 (페이지 새로고침 후 복구 시)
  const remaining = PENDING_TIMEOUT - elapsed;
  pendingTimeoutId = setTimeout(() => {
    // 아직 pending 상태이고 로딩 UI가 있으면 제거
    if (chatStatus.isPending(chatId)) {
      chatStatus.clearPending(chatId);
      document.getElementById('loadingMessage')?.remove();
      refreshChatListBadges();
      console.log('[loadChat] pending 타임아웃으로 자동 해제:', chatId);
    }
  }, remaining);
}

// ============================================
// World Info Modal
// ============================================

export function showWorldInfoModal() {
  const worldBtn = document.getElementById('worldInfoBtn');
  const data = worldBtn._worldData;

  if (!data) return;

  const { world, characters, scenario } = data;
  const validChars = (characters || []).filter(c => c);

  const modalContent = document.getElementById('worldInfoModalContent');
  modalContent.innerHTML = `
    <div class="world-info" style="margin-bottom: 0;">
      <div class="world-title">${escapeHtml(world.title)}</div>
      <div style="font-size: 12px; color: var(--accent); margin-bottom: 8px;">${escapeHtml(world.genre)} · ${escapeHtml(world.setting || '')}</div>
      <div class="world-desc">${escapeHtml(world.description)}</div>

      <div style="margin-bottom: 12px; margin-top: 16px;">
        <div style="font-size: 12px; color: var(--text-secondary); margin-bottom: 8px;">등장인물</div>
        ${validChars.map(c => `
          <div class="char-status-card ${c.imageUrl ? 'has-image' : ''}">
            ${c.imageUrl ? `
              <div class="char-card-image" data-action="showImage" data-url="${c.imageUrl}" data-name="${escapeHtml(c.name)}">
                <div class="img-loading-wrapper" style="width:100%;height:100%"><div class="spinner"></div><img src="${c.imageUrl}" alt="${escapeHtml(c.name)}" style="width:100%;height:100%;object-fit:cover" onload="this.classList.add('loaded');this.parentElement&&this.parentElement.classList.add('loaded')"></div>
              </div>
            ` : ''}
            <div class="char-status-header">
              <div class="char-status-avatar">${c.imageUrl ? `<img src="${c.imageUrl}" alt="${escapeHtml(c.name)}" data-action="showImage" data-url="${c.imageUrl}" data-name="${escapeHtml(c.name)}">` : (c.gender === '여성' ? '👩' : '👨')}</div>
              <div>
                <div class="char-status-name">${escapeHtml(c.name)}</div>
                <div class="char-status-role">${escapeHtml(c.role)} · ${escapeHtml(c.gender)} · ${escapeHtml(String(c.age))}세</div>
              </div>
            </div>
            <div style="font-size: 12px; color: var(--text-secondary); margin-top: 8px;">
              ${escapeHtml(c.personality)}
            </div>
          </div>
        `).join('')}
      </div>

      ${scenario.mainQuest ? `
        <div class="scenario-box" style="background: rgba(0,0,0,0.3);">
          <div class="label">목표</div>
          <div class="content">${escapeHtml(scenario.mainQuest)}</div>
        </div>
      ` : ''}
    </div>
  `;

  document.getElementById('worldInfoModal').style.display = 'flex';
  if (window.ModalManager) {
    window.ModalManager.push('worldInfoModal');
  }
}

// ============================================
// World Info Display (in message container)
// ============================================

export function showWorldInfo(world, characters, scenario) {
  const container = document.getElementById('messagesContainer');
  const validChars = (characters || []).filter(c => c);

  // Remove empty state
  const emptyState = container.querySelector('.empty-state');
  if (emptyState) emptyState.remove();

  // 이미지 가져오기
  const chatData = getCurrentChatData();
  const images = chatData?.images || { characters: [], location: null };

  const worldHtml = `
    <div class="world-info">
      ${images.location ? `
        <div class="location-image-container">
          <div class="img-loading-wrapper" style="width:100%;height:100%"><div class="spinner"></div><img src="${images.location}" alt="장소" class="location-image" onload="this.classList.add('loaded');this.parentElement&&this.parentElement.classList.add('loaded')" onerror="this.parentElement&&(this.parentElement.style.display='none')"></div>
        </div>
      ` : ''}
      <div class="world-title">${escapeHtml(world.title)}</div>
      <div class="world-desc">${escapeHtml(world.description)}</div>

      <div style="margin: 16px 0 12px;">
        <div style="font-size: 12px; color: var(--text-secondary); margin-bottom: 8px;">등장인물</div>
        <div class="characters-grid">
          ${validChars.map((c, idx) => `
            <div class="character-card-mini">
              ${images.characters[idx] ? `
                <div class="img-loading-wrapper character-card-img-wrapper"><div class="spinner"></div><img src="${images.characters[idx]}" alt="${escapeHtml(c.name)}" class="character-card-image" onload="this.classList.add('loaded');this.parentElement&&this.parentElement.classList.add('loaded')" onerror="if(this.parentElement){this.parentElement.style.display='none';if(this.parentElement.parentElement){var fb=this.parentElement.parentElement.querySelector('.character-card-fallback');if(fb)fb.style.display='flex'}}"></div>
                <div class="character-card-fallback" style="display:none;"></div>
              ` : `
                <div class="character-card-fallback"></div>
              `}
              <div class="character-card-info">
                <div class="character-card-name">${escapeHtml(c.name)}</div>
                <div class="character-card-role">${escapeHtml(c.role)}</div>
                <div class="character-card-personality">${escapeHtml(c.personality)}</div>
              </div>
            </div>
          `).join('')}
        </div>
      </div>

      <div class="scenario-box" style="background: rgba(0,0,0,0.3);">
        <div class="label">상황</div>
        <div class="content">${escapeHtml(scenario.situation)}</div>
      </div>
    </div>
  `;

  container.insertAdjacentHTML('afterbegin', worldHtml);
}

// ============================================
// Character Profile Display
// ============================================

export function showCharacterProfile(character, scenario) {
  const container = document.getElementById('messagesContainer');

  // Remove empty state
  const emptyState = container.querySelector('.empty-state');
  if (emptyState) emptyState.remove();

  // 이미지 가져오기
  const chatData = getCurrentChatData();
  const images = chatData?.images || { characters: [] };
  const characterImage = images.characters?.[0] || null;

  // 이모지 아바타 선택 (이미지 없을 때 폴백)
  const avatarEmojis = {
    '남성': ['👨', '🧑', '👱‍♂️', '🧔', '👨‍🦱'],
    '여성': ['👩', '👱‍♀️', '👩‍🦰', '👩‍🦱', '🧑‍🦰']
  };
  const emojis = avatarEmojis[character.gender] || avatarEmojis['남성'];
  const avatar = emojis[Math.floor(Math.random() * emojis.length)];

  const safeName = escapeHtml(character.name);
  const safeAge = escapeHtml(String(character.age));
  const safeRelationship = escapeHtml(character.relationship || '');

  const profileHtml = `
    <div class="character-profile-card">
      ${characterImage ? `
        <div class="character-hero-image" data-action="showImage" data-url="${characterImage}">
          <div class="img-loading-wrapper" style="width:100%;height:100%"><div class="spinner"></div><img src="${characterImage}" alt="${safeName}" style="width:100%;height:100%;object-fit:cover" onload="this.classList.add('loaded');this.parentElement&&this.parentElement.classList.add('loaded')" onerror="if(this.parentElement&&this.parentElement.parentElement)this.parentElement.parentElement.innerHTML='<div class=\\'character-hero-fallback\\'>${avatar}</div>'"></div>
          <div class="character-hero-overlay">
            <div class="character-hero-name">${safeName}</div>
          </div>
        </div>
      ` : `
        <div class="character-hero-fallback-container">
          <div class="character-hero-fallback">${avatar}</div>
          <div class="character-hero-name">${safeName}</div>
        </div>
      `}
    </div>
  `;

  container.insertAdjacentHTML('afterbegin', profileHtml);
}

// ============================================
// Image Modal
// ============================================

export function showImageModal(imageUrl, name = '점술가') {
  const existingModal = document.getElementById('imageModal');
  if (existingModal) existingModal.remove();

  const safeName = escapeHtml(name);

  const modal = document.createElement('div');
  modal.id = 'imageModal';
  modal.className = 'image-modal-overlay';
  modal.onclick = () => modal.remove();
  modal.innerHTML = `
    <div class="image-modal-content">
      <div class="img-loading-wrapper" style="display:flex;align-items:center;justify-content:center;min-width:200px;min-height:200px"><div class="spinner" style="width:32px;height:32px"></div><img src="${imageUrl}" alt="${safeName}" style="max-width:90vw;max-height:80vh;border-radius:12px" onload="this.classList.add('loaded');this.parentElement&&this.parentElement.classList.add('loaded')"></div>
      <button class="image-modal-close" data-action="close">×</button>
    </div>
  `;
  document.body.appendChild(modal);

  // 이벤트 위임
  modal.addEventListener('click', (e) => {
    // 모달 내용 클릭 시 전파 중단 (배경 클릭과 구분)
    if (e.target.closest('.image-modal-content') && !e.target.closest('[data-action="close"]')) {
      e.stopPropagation();
      return;
    }
    if (e.target.closest('[data-action="close"]')) {
      modal.remove();
    }
  });
}

// ============================================
// Helper: waitForImagesToLoad 제거됨 (2026-01-11)
// ResizeObserver 기반 setupScrollObserver로 대체
// 이미지 로드 대기 없이 즉시 모달 오픈, 스크롤은 자동 조정
// ============================================

// ============================================
// window 노출 제거됨 (2026-01-06)
// 모든 호출이 ES6 import로 처리됨
// ============================================
